import React, { useEffect, useRef, useState } from 'react';

import {
  HighlightBox,
  ScriptBox,
  SectionHeader,
  TabSection,
  WarningBox,
} from './_shared';

type NavSection = {
  id: string;
  label: string;
  tag: string;
};

const NAV_SECTIONS: NavSection[] = [
  { id: 'intro', label: 'Introduction', tag: '1' },
  { id: 'qualification', label: 'Qualification', tag: '2' },
  { id: 'cas-vente', label: 'Cas vente', tag: 'A' },
  { id: 'cas-location', label: 'Cas location', tag: 'B' },
  { id: 'closing', label: 'Closing', tag: 'C' },
];

function Scripts() {
  const [activeSectionId, setActiveSectionId] = useState<string>('intro');

  const introRef = useRef<HTMLDivElement>(null);
  const qualificationRef = useRef<HTMLDivElement>(null);
  const casVenteRef = useRef<HTMLDivElement>(null);
  const casLocationRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef<HTMLDivElement>(null);

  const sentinelRefs: Record<string, React.RefObject<HTMLDivElement>> = {
    intro: introRef,
    qualification: qualificationRef,
    'cas-vente': casVenteRef,
    'cas-location': casLocationRef,
    closing: closingRef,
  };

  useEffect(() => {
    const scrollContainer = document.getElementById('mainScrollable') ?? window;
    const orderedIds = NAV_SECTIONS.map((s) => s.id);
    let rafId = 0;

    const update = () => {
      const container = scrollContainer instanceof HTMLElement ? scrollContainer : document.documentElement;
      const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      if (isAtBottom) {
        setActiveSectionId(orderedIds[orderedIds.length - 1]);
        return;
      }

      let current = orderedIds[0];
      for (const id of orderedIds) {
        const el = sentinelRefs[id]?.current;
        if (el && el.getBoundingClientRect().top <= 120) {
          current = id;
        }
      }
      setActiveSectionId(current);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      scrollContainer.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const scrollToSection = (id: string) => {
    sentinelRefs[id]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="grid grid-cols-[180px_1fr] items-start gap-0">
      {/* Left sticky navigation */}
      <nav className="sticky top-28 py-6 pr-0 pl-1">
        <div className="mb-3 pl-3 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
          Sommaire
        </div>
        <ul className="list-none space-y-0.5">
          {NAV_SECTIONS.map((section, idx) => (
            <React.Fragment key={section.id}>
              {idx === 2 && <div className="mx-3 my-2 h-px bg-gray-200" />}
              {idx === 4 && <div className="mx-3 my-2 h-px bg-gray-200" />}
              <li>
                <button
                  onClick={() => scrollToSection(section.id)}
                  className={`flex w-full items-center gap-2 rounded px-3 py-1.5 text-left text-xs transition-all ${
                    activeSectionId === section.id
                      ? 'bg-blue-50 font-medium text-rgpd-blue'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-rgpd-blue'
                  }`}
                >
                  <span
                    className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                      activeSectionId === section.id
                        ? 'bg-rgpd-blue text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {section.tag}
                  </span>
                  <span className="leading-tight">{section.label}</span>
                </button>
              </li>
            </React.Fragment>
          ))}
        </ul>
      </nav>

      {/* Main content */}
      <div className="border-l border-gray-200 pl-8">
        {/* Objective banner */}
        <div className="mb-8 flex gap-4 rounded-lg bg-gray-200 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-white/10 text-lg">
            <span role="img" aria-label="target">🎯</span>
          </div>
          <div>
            <div className="mb-1.5 text-[10px] font-semibold tracking-widest text-gray-900 uppercase">
              Objectif de l{"'"}appel
            </div>
            <p className="text-sm leading-relaxed text-gray-700">
              <strong className="text-rgpd-blue-light">Qualifier</strong> le projet du copropriétaire ·{' '}
              <strong className="text-rgpd-blue-light">Apporter de la valeur</strong> dès le premier contact ·{' '}
              <strong className="text-rgpd-blue-light">Créer le besoin</strong> autour de l{"'"}accompagnement syndic ·{' '}
              <strong className="text-rgpd-blue-light">Obtenir un rendez-vous</strong>
            </p>
          </div>
        </div>

        {/* Meta info bar */}
        <div className="mb-8 flex flex-wrap gap-6 rounded-lg border border-gray-200 bg-gray-50 px-6 py-4">
          {[
            { label: 'Délai de relance', value: 'J+7 à J+10' },
            { label: 'Durée estimée', value: '5 à 15 minutes' },
            { label: 'Déclencheur', value: 'DPE détecté ADEME' },
            { label: 'Objectif', value: 'Qualification + RDV' },
          ].map((meta, i) => (
            <div key={i} className={`pr-6 ${i < 3 ? 'border-r border-gray-200' : ''}`}>
              <div className="text-[10px] font-semibold tracking-widest text-gray-400 uppercase">{meta.label}</div>
              <div className="mt-0.5 text-sm font-medium text-rgpd-blue">{meta.value}</div>
            </div>
          ))}
        </div>

        {/* ─── SECTION 1 : INTRODUCTION ─── */}
        <div ref={introRef} data-section="intro" className="h-px" />
        <section id="intro" className="mb-10 scroll-mt-28">
          <TabSection>
            <SectionHeader title={<span>Étape 1 — Introduction & rappel du mail</span>} />
            <p className="mb-5 text-sm text-gray-400 italic">
              Prise de contact directe. Rappel du contexte du mail envoyé préalablement suite à la détection du DPE sur l{"'"}ADEME.
            </p>

            <ScriptBox>
              <div className="mb-1 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
                Script d{"'"}accroche
              </div>
              <p>Bonjour Monsieur / Madame <em className="font-medium text-rgpd-blue not-italic">[Nom]</em>,</p>
              <p>
                Je suis le <strong>référent mutation location de votre syndic</strong>, je me permets de vous appeler.
              </p>
              <p>
                Je vous contacte suite à un <strong>Diagnostic de Performance Énergétique réalisé récemment</strong> dans votre immeuble, qui pourrait concerner votre appartement, et pour lequel je vous avais envoyé un mail.
              </p>
            </ScriptBox>

            {/* Pivot question */}
            <div className="my-4 flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <span className="shrink-0 text-2xl font-light text-rgpd-blue">?</span>
              <p className="text-[15px] leading-snug font-medium text-slate-800">
                Je voulais simplement vérifier avec vous : <em className="font-normal text-gray-500 italic">est-ce que vous êtes bien à l{"'"}origine de ce DPE pour votre appartement ?</em>
              </p>
            </div>

            {/* Branching */}
            <div className="my-5 flex items-center gap-2 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
              <div className="h-px flex-1 bg-gray-200" />
              Réponses possibles
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <div className="mb-5 grid grid-cols-2 gap-4">
              {/* NON card */}
              <div className="overflow-hidden rounded-lg">
                <div className="flex items-center gap-2 bg-red-700 px-4 py-2.5 text-[11px] font-semibold tracking-wide text-white/90 uppercase">
                  <span role="img" aria-label="cross mark">✗</span> Non — DPE non confirmé
                </div>
                <div className="border border-t-0 border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-500">
                  <p className="mb-2"><strong className="text-gray-800">Fin d{"'"}appel courtoise.</strong> Vous remerciez et proposez votre disponibilité pour un projet futur.</p>
                  <p className="mb-2">
                    « Très bien, merci pour votre retour. Dans tous les cas, en tant que référent mutation location du syndic, je reste à votre disposition si vous avez un projet, que ce soit pour une <strong className="text-gray-800">vente ou une mise en location</strong>. »
                  </p>
                  <p>Rappelez l{"'"}offre des rapports gratuits et laissez vos coordonnées.</p>
                </div>
              </div>

              {/* OUI card */}
              <div className="overflow-hidden rounded-lg">
                <div className="flex items-center gap-2 bg-rgpd-green px-4 py-2.5 text-[11px] font-semibold tracking-wide text-white/90 uppercase">
                  <span role="img" aria-label="check mark">✓</span> Oui — DPE confirmé
                </div>
                <div className="border border-t-0 border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-500">
                  <p className="mb-2"><strong className="text-gray-800">Confirmation obtenue.</strong> L{"'"}appel se poursuit vers la qualification du projet.</p>
                  <p className="mb-2">« Parfait, merci pour votre confirmation. »</p>
                  <p>→ Passer à l{"'"}<strong className="text-gray-800">Étape 2 : Qualification</strong></p>
                </div>
              </div>
            </div>

            <HighlightBox variant="warning">
              <h4 className="mb-1 text-xs font-bold tracking-wide uppercase">Conseil</h4>
              <p>
                Si le copropriétaire ne se souvient pas du mail, renvoyez-lui brièvement le contexte : collecte des DPE par le syndic via l{"'"}ADEME pour la préparation du DPE collectif et du PPT de l{"'"}immeuble. Cela rassure et légitime votre appel.
              </p>
            </HighlightBox>
          </TabSection>
        </section>

        <div className="my-10 h-px bg-gray-200" />

        {/* ─── SECTION 2 : QUALIFICATION ─── */}
        <div ref={qualificationRef} data-section="qualification" className="h-px" />
        <section id="qualification" className="mb-10 scroll-mt-28">
          <TabSection>
            <SectionHeader title={<span>Étape 2 — Qualification du projet</span>} />
            <p className="mb-5 text-sm text-gray-400 italic">
              DPE confirmé. Identifier la nature du projet pour adapter l{"'"}argumentation.
            </p>

            <div className="my-4 flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <span className="shrink-0 text-2xl font-light text-rgpd-blue">?</span>
              <p className="text-[15px] leading-snug font-medium text-slate-800">
                Est-ce que ce DPE s{"'"}inscrit dans le cadre d{"'"}un projet de <em className="font-normal text-gray-500 italic">vente</em> ou de <em className="font-normal text-gray-500 italic">mise en location</em> ?
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="overflow-hidden rounded-lg">
                <div className="flex items-center gap-2 bg-rgpd-blue-dark px-4 py-2.5 text-[11px] font-semibold tracking-wide text-white/90 uppercase">
                  <span role="img" aria-label="sale">🏷</span>&ensp;Projet de vente
                </div>
                <div className="border border-t-0 border-gray-200 bg-white p-4 text-sm text-gray-500">
                  <p>→ Aller au <strong className="text-gray-800">Cas A — Vente</strong></p>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg">
                <div className="flex items-center gap-2 bg-rgpd-green px-4 py-2.5 text-[11px] font-semibold tracking-wide text-white/90 uppercase">
                  <span role="img" aria-label="key">🔑</span>&ensp;Projet de location
                </div>
                <div className="border border-t-0 border-gray-200 bg-white p-4 text-sm text-gray-500">
                  <p>→ Aller au <strong className="text-gray-800">Cas B — Location</strong></p>
                </div>
              </div>
            </div>
          </TabSection>
        </section>

        <div className="my-10 h-px bg-gray-200" />

        {/* ─── CAS A : VENTE ─── */}
        <div ref={casVenteRef} data-section="cas-vente" className="h-px" />
        <section id="cas-vente" className="mb-10 scroll-mt-28">
          <TabSection>
            <SectionHeader title={<span>Cas A — Argumentation — Projet de vente</span>} />
            <p className="mb-5 text-sm text-gray-400 italic">
              Deux axes complémentaires : la sécurisation juridique de la vente et le positionnement marché du bien.
            </p>

            <ScriptBox>
              <div className="mb-1 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
                Accroche
              </div>
              <p>
                Très bien. Dans le cadre d{"'"}une vente, il y a généralement <strong>deux points importants à évoquer ensemble</strong>.
              </p>
            </ScriptBox>

            {/* Argument 1 */}
            <div className="mb-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rgpd-blue text-sm font-medium text-white">1</span>
                <span className="text-sm font-semibold text-slate-800">Les documents obligatoires — l{"'"}état daté</span>
              </div>
              <div className="space-y-3 p-5 text-sm leading-relaxed text-gray-500">
                <p>
                  C{"'"}est un document que le syndic est <strong className="text-gray-800">seul en mesure de préparer</strong>, et qui est surtout très utile car il regroupe des informations essentielles : charges, travaux votés, situation de la copropriété…
                </p>
                <p>
                  Ces éléments sont <strong className="text-gray-800">systématiquement analysés par le notaire</strong> et peuvent avoir un impact concret sur la vente.
                </p>
                <WarningBox>
                  <p>
                    <strong>Point d{"'"}attention :</strong> Si le notaire de l{"'"}acquéreur découvre des charges à venir — ravalement, travaux PPPT — dont l{"'"}acheteur n{"'"}avait pas connaissance, <strong>cela peut créer des difficultés sur le prix estimé</strong>, parfois juste avant la signature définitive.
                  </p>
                </WarningBox>
                <p>
                  C{"'"}est pour cela qu{"'"}au-delà de l{"'"}analyse du bien lui-même, il est essentiel de le <strong className="text-gray-800">replacer dans le contexte de la copropriété</strong> pour avoir une vision fiable. Sans cela, on ne met pas toutes les chances de son côté pour sécuriser la vente.
                </p>
              </div>
            </div>

            {/* Argument 2 */}
            <div className="mb-5 overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rgpd-blue text-sm font-medium text-white">2</span>
                <span className="text-sm font-semibold text-slate-800">Positionnement marché — les rapports gratuits</span>
              </div>
              <div className="space-y-3 p-5 text-sm leading-relaxed text-gray-500">
                <p>
                  Le second point concerne <strong className="text-gray-800">l{"'"}immeuble dans son environnement</strong>, c{"'"}est-à-dire son positionnement par rapport au marché local.
                </p>

                {/* Rapports box */}
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-2.5 flex items-center gap-1.5 text-[10px] font-bold tracking-wide text-blue-600 uppercase">
                    <span role="img" aria-label="folder">📁</span> Rapports préparés gratuitement
                  </div>
                  <ul className="mb-3 list-none space-y-1.5">
                    <li className="relative pl-5 text-sm text-blue-900 before:absolute before:left-0 before:font-semibold before:text-blue-600 before:content-['—']">
                      Les DPE réalisés dans votre immeuble depuis 2021
                    </li>
                    <li className="relative pl-5 text-sm text-blue-900 before:absolute before:left-0 before:font-semibold before:text-blue-600 before:content-['—']">
                      Les ventes sur les 4 dernières années dans l{"'"}immeuble et dans un rayon de 150 mètres
                    </li>
                  </ul>
                  <span className="inline-flex items-center gap-1.5 rounded bg-green-100 px-3 py-1 text-[11px] font-bold tracking-wide text-green-800 uppercase">
                    <span role="img" aria-label="check mark">✓</span> Gratuit — sans engagement
                  </span>
                </div>

                <p>
                  Ce rapport permet de <strong className="text-gray-800">comparer les appartements entre eux</strong> et de situer concrètement votre bien sur le marché.
                </p>
                <p className="text-gray-400 italic">
                  Il ne constitue pas un avis de valeur, mais il permet d{"'"}avoir une vision claire des tendances et du positionnement réel du bien.
                </p>
              </div>
            </div>

            {/* Pivot question: estimation */}
            <div className="my-4 flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <span className="shrink-0 text-2xl font-light text-rgpd-blue">?</span>
              <p className="text-[15px] leading-snug font-medium text-slate-800">
                D{"'"}ailleurs, <em className="font-normal text-gray-500 italic">est-ce que vous avez déjà fait estimer votre bien ?</em>
              </p>
            </div>

            <div className="my-5 flex items-center gap-2 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
              <div className="h-px flex-1 bg-gray-200" />
              Selon la réponse
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Oui — estimation déjà réalisée */}
              <div className="overflow-hidden rounded-lg">
                <div className="flex items-center gap-2 bg-rgpd-green px-4 py-2.5 text-[11px] font-semibold tracking-wide text-white/90 uppercase">
                  <span role="img" aria-label="check mark">✓</span> Oui — Estimation déjà réalisée
                </div>
                <div className="border border-t-0 border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-500">
                  <p className="mb-2">
                    « De mon côté, à ma connaissance, nous n{"'"}avons pas été sollicités par l{"'"}agence pour récupérer les éléments liés à l{"'"}immeuble. »
                  </p>
                  <p className="mb-2">
                    Or, ces informations — charges, travaux, situation de la copropriété — sont essentielles et se retrouvent dans <strong className="text-gray-800">l{"'"}état daté</strong>. Elles sont systématiquement analysées par le notaire et peuvent avoir un impact concret sur la transaction.
                  </p>
                  <p className="mb-3">
                    Sans ces éléments, l{"'"}évaluation peut être <strong className="text-gray-800">partielle ou imprécise</strong>.
                  </p>
                  <HighlightBox variant="warning">
                    <h4 className="mb-1 text-xs font-bold tracking-wide uppercase">Argument pivot</h4>
                    <p>« Il serait peut-être souhaitable d{"'"}en faire une autre avec une personne qui connaît bien l{"'"}immeuble. »</p>
                  </HighlightBox>
                </div>
              </div>

              {/* Non — pas encore d'estimation */}
              <div className="overflow-hidden rounded-lg">
                <div className="flex items-center gap-2 bg-red-700 px-4 py-2.5 text-[11px] font-semibold tracking-wide text-white/90 uppercase">
                  <span role="img" aria-label="cross mark">✗</span> Non — Pas encore d{"'"}estimation
                </div>
                <div className="border border-t-0 border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-500">
                  <p className="mb-2">
                    « Justement, l{"'"}intérêt des rapports est de vous apporter une <strong className="text-gray-800">vision claire et complète avant toute mise en vente</strong>. »
                  </p>
                  <p>
                    Et si vous le souhaitez, je peux demander à une personne au cabinet — qui connaît bien l{"'"}immeuble — de vous en faire une, <strong className="text-gray-800">sans aucun engagement bien sûr</strong>.
                  </p>
                </div>
              </div>
            </div>
          </TabSection>
        </section>

        <div className="my-10 h-px bg-gray-200" />

        {/* ─── CAS B : LOCATION ─── */}
        <div ref={casLocationRef} data-section="cas-location" className="h-px" />
        <section id="cas-location" className="mb-10 scroll-mt-28">
          <TabSection>
            <SectionHeader title={<span>Cas B — Argumentation — Projet de location</span>} />
            <p className="mb-5 text-sm text-gray-400 italic">
              Deux axes : la conformité documentaire et l{"'"}optimisation de la mise sur le marché locatif.
            </p>

            <ScriptBox>
              <div className="mb-1 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
                Accroche
              </div>
              <p>
                Très bien. Dans le cadre d{"'"}une mise en location, il y a également <strong>deux points importants à anticiper</strong>.
              </p>
            </ScriptBox>

            {/* Argument 1 */}
            <div className="mb-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rgpd-blue text-sm font-medium text-white">1</span>
                <span className="text-sm font-semibold text-slate-800">Documents à fournir — extrait du règlement de copropriété</span>
              </div>
              <div className="space-y-3 p-5 text-sm leading-relaxed text-gray-500">
                <p>
                  Le premier concerne les <strong className="text-gray-800">documents à fournir</strong>, notamment un extrait du règlement de copropriété que <strong className="text-gray-800">le syndic peut vous préparer</strong>.
                </p>
              </div>
            </div>

            {/* Argument 2 */}
            <div className="mb-5 overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rgpd-blue text-sm font-medium text-white">2</span>
                <span className="text-sm font-semibold text-slate-800">Performance énergétique & positionnement locatif</span>
              </div>
              <div className="space-y-3 p-5 text-sm leading-relaxed text-gray-500">
                <p>
                  Le second concerne le <strong className="text-gray-800">positionnement de votre bien</strong>, notamment au regard de sa performance énergétique — un critère de plus en plus déterminant pour les locataires et la réglementation.
                </p>

                {/* Rapports box */}
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="mb-2.5 flex items-center gap-1.5 text-[10px] font-bold tracking-wide text-blue-600 uppercase">
                    <span role="img" aria-label="folder">📁</span> Rapports préparés gratuitement
                  </div>
                  <ul className="mb-3 list-none space-y-1.5">
                    <li className="relative pl-5 text-sm text-blue-900 before:absolute before:left-0 before:font-semibold before:text-blue-600 before:content-['—']">
                      Les DPE réalisés dans votre immeuble depuis 2021
                    </li>
                    <li className="relative pl-5 text-sm text-blue-900 before:absolute before:left-0 before:font-semibold before:text-blue-600 before:content-['—']">
                      Un aperçu du positionnement des biens comparables sur le secteur
                    </li>
                  </ul>
                  <span className="inline-flex items-center gap-1.5 rounded bg-green-100 px-3 py-1 text-[11px] font-bold tracking-wide text-green-800 uppercase">
                    <span role="img" aria-label="check mark">✓</span> Gratuit — permet d{"'"}anticiper les contraintes
                  </span>
                </div>

                <p>
                  Cela vous permet d{"'"}<strong className="text-gray-800">anticiper d{"'"}éventuelles contraintes réglementaires</strong> et d{"'"}optimiser votre mise en location.
                </p>
              </div>
            </div>

            {/* Pivot question */}
            <div className="my-4 flex gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <span className="shrink-0 text-2xl font-light text-rgpd-blue">?</span>
              <p className="text-[15px] leading-snug font-medium text-slate-800">
                <em className="font-normal text-gray-500 italic">Faites-vous vous-même la gestion de la location de votre bien ?</em>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="overflow-hidden rounded-lg">
                <div className="flex items-center gap-2 bg-rgpd-green px-4 py-2.5 text-[11px] font-semibold tracking-wide text-white/90 uppercase">
                  <span role="img" aria-label="check mark">✓</span> Oui — Gestion directe
                </div>
                <div className="border border-t-0 border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-500">
                  <p>
                    Accompagner sur les documents, le positionnement marché et la conformité DPE. Rester disponible comme interlocuteur privilégié du syndic.
                  </p>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg">
                <div className="flex items-center gap-2 bg-red-700 px-4 py-2.5 text-[11px] font-semibold tracking-wide text-white/90 uppercase">
                  <span role="img" aria-label="cross mark">✗</span> Non — Gestion déléguée
                </div>
                <div className="border border-t-0 border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-500">
                  <p>
                    Explorer la possibilité que le cabinet propose une solution de gestion locative, en valorisant la connaissance de l{"'"}immeuble.
                  </p>
                </div>
              </div>
            </div>
          </TabSection>
        </section>

        <div className="my-10 h-px bg-gray-200" />

        {/* ─── CLOSING ─── */}
        <div ref={closingRef} data-section="closing" className="h-px" />
        <section id="closing" className="mb-10 scroll-mt-28">
          <TabSection>
            <SectionHeader title={<span>Closing — Prise de rendez-vous</span>} />
            <p className="mb-5 text-sm text-gray-400 italic">
              Commun aux deux cas (vente et location). L{"'"}objectif est de transformer l{"'"}appel en rendez-vous physique ou téléphonique.
            </p>

            <div className="rounded-lg bg-rgpd-blue-darker p-6">
              <h4 className="mb-3 text-lg font-normal text-white">
                Script de closing
              </h4>
              <p className="mb-3 text-sm leading-relaxed text-white/65">
                En tant que référent mutation location, je suis là pour vous accompagner sur ces deux aspects.
              </p>
              <p className="mb-4 text-sm leading-relaxed text-white/65">
                Je vous propose d{"'"}en échanger ensemble — <strong className="text-rgpd-blue-light">15 à 30 minutes</strong> — afin de vous apporter une lecture claire et adaptée à votre situation, <strong className="text-rgpd-blue-light">après vous avoir préparé les rapports dont je vous parlais</strong>.
              </p>

              <div className="rounded border border-rgpd-blue-light/30 bg-rgpd-blue-light/10 p-4">
                <div className="mb-2 text-[10px] font-bold tracking-widest text-rgpd-blue-light/70 uppercase">
                  Question de closing
                </div>
                <p className="text-lg leading-snug text-blue-200 italic">
                  « Est-ce que vous seriez plutôt disponible en début ou en fin de semaine ? »
                </p>
              </div>
            </div>

            <HighlightBox variant="warning">
              <h4 className="mb-1 text-xs font-bold tracking-wide uppercase">Technique de closing — choix binaire</h4>
              <p>
                Proposer un choix entre deux options (début / fin de semaine) plutôt qu{"'"}une question ouverte évite le report et obtient un engagement plus naturel. C{"'"}est une alternative directe au classique « je vous rappellerai » qui réduit la conversion.
              </p>
            </HighlightBox>
          </TabSection>
        </section>
      </div>
    </div>
  );
}

export default Scripts;
